/**
 * One easing vocabulary for every guide motion, in JS for the rAF flights and mirrored as custom
 * properties in fox-guide.css for the CSS pieces. Durations come from a six-step scale only.
 */
export const DUR = { xs: 120, s: 180, m: 280, l: 450, xl: 700, xxl: 1100 } as const;

/** Cubic bezier solver (same maths as CSS cubic-bezier). */
function bezier(x1: number, y1: number, x2: number, y2: number) {
  const ax = 1 - 3 * x2 + 3 * x1;
  const bx = 3 * x2 - 6 * x1;
  const cx = 3 * x1;
  const ay = 1 - 3 * y2 + 3 * y1;
  const by = 3 * y2 - 6 * y1;
  const cy = 3 * y1;
  const sx = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sy = (t: number) => ((ay * t + by) * t + cy) * t;
  const dx = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  return (x: number) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 6; i += 1) {
      const d = dx(t);
      if (Math.abs(d) < 1e-6) break;
      t -= (sx(t) - x) / d;
    }
    return sy(Math.min(1, Math.max(0, t)));
  };
}

export const easeOut = bezier(0.2, 0.7, 0.2, 1);
export const easeInOut = bezier(0.45, 0.05, 0.2, 1);
export const easeIn = bezier(0.5, 0, 0.8, 0.3);
/** The CSS linear() spring, sampled: 0, .3 @10%, .7 @20%, 1.02 @35%, .98 @55%, 1. */
export function easeSpring(x: number): number {
  const pts: [number, number][] = [[0, 0], [0.1, 0.3], [0.2, 0.7], [0.35, 1.02], [0.55, 0.98], [1, 1]];
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  for (let i = 1; i < pts.length; i += 1) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    if (x <= x1) return y0 + ((x - x0) / (x1 - x0)) * (y1 - y0);
  }
  return 1;
}

/** Slow-motion hook for the recordings: scales every guide duration (rAF and CSS). */
export function timeScale(): number {
  const w = window as unknown as { __fxTimeScale?: number };
  return typeof w.__fxTimeScale === 'number' && w.__fxTimeScale > 0 ? w.__fxTimeScale : 1;
}
